// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hatch — the three modes (principle #14). The RULE SET is the axis that changes the game,
// not a dialled number:
//   · Zen     — one 10×10 picture, NO clock, NO death. Mistakes only cost score. The calm,
//               learn-it, showcase mode; the daily lives here. Goal: reveal the picture.
//   · Blitz   — a stream of small 5×5 pictures against a 90-second clock. Race the CLOCK:
//               bank as many pictures as you can; a mistake costs seconds. A different GOAL
//               (throughput), not one careful reveal.
//   · Strikes — one 8×8 picture, THREE mistakes and the run ENDS. Sudden death: you must be
//               CERTAIN before you fill, so deduction (and marking) is load-bearing. A
//               structurally different risk curve.
//
// The host's chosen mode travels FROZEN inside the round start (rematch roundOpts); guests
// render state().hostOpts. An id off the wire is always modeOf()-validated so an unknown
// value falls back to Zen rather than reaching the generator as undefined (guarded with
// Object.hasOwn — a wire key must never resolve a prototype member).

export type ModeId = 'zen' | 'blitz' | 'strikes';

export type Clock = 'up' | 'down';

export interface Mode {
  readonly id: ModeId;
  readonly name: string;
  readonly blurb: string;
  /** Grid is size×size. */
  readonly size: number;
  /** 'up' counts elapsed (Zen/Strikes); 'down' drains from durationMs (Blitz). */
  readonly clock: Clock;
  /** Blitz only: the round length in ms. 0 otherwise. */
  readonly durationMs: number;
  /** Strikes: this many mistakes ends the run. 0 = unlimited (Zen/Blitz). */
  readonly maxStrikes: number;
  /** Blitz: solve boards back-to-back; a completed board banks and a fresh one deals. */
  readonly stream: boolean;
  /** Target fill fraction the generator aims for. Pinned solvable by the sim. */
  readonly density: number;
  /** Reference solve time (ms) for the completion time-bonus (Zen/Strikes). */
  readonly targetMs: number;
}

export const MODES: Record<ModeId, Mode> = {
  zen: {
    id: 'zen',
    name: 'Zen',
    blurb: 'One 10×10 picture. No clock, no strikes — take your time and read the numbers. A wrong fill only costs points.',
    size: 10,
    clock: 'up',
    durationMs: 0,
    maxStrikes: 0,
    stream: false,
    density: 0.52,
    targetMs: 240_000,
  },
  blitz: {
    id: 'blitz',
    name: 'Blitz',
    blurb: 'Ninety seconds, a stream of 5×5 pictures. Bank as many as you can — every mistake costs you a couple of seconds.',
    size: 5,
    clock: 'down',
    durationMs: 90_000,
    maxStrikes: 0,
    stream: true,
    density: 0.56,
    targetMs: 0,
  },
  strikes: {
    id: 'strikes',
    name: 'Strikes',
    blurb: 'One 8×8 picture, three strikes. A wrong fill is a strike — the third ends the run. Be sure before you commit.',
    size: 8,
    clock: 'up',
    durationMs: 0,
    maxStrikes: 3,
    stream: false,
    density: 0.54,
    targetMs: 150_000,
  },
};

export const MODE_IDS: ModeId[] = ['zen', 'blitz', 'strikes'];

export const DEFAULT_MODE: Mode = MODES.zen;

/** Resolve a (possibly untrusted, off-the-wire) id to a Mode; unknown → Zen. */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) {
    return MODES[id as ModeId];
  }
  return DEFAULT_MODE;
}
